import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Loader, 
  Edit2, 
  Trash2, 
  ArrowUp, 
  ArrowDown, 
  Camera,
  AlertCircle,
  Save,
  Building
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ui/toast/use-toast';
import MapLocation from '../components/MapLocation';
import { Card, CardContent } from '../components/ui/card';

// Componentes UI integrados directamente
const Label = ({ className, htmlFor, children, ...props }) => {
  return (
    <label
      htmlFor={htmlFor}
      className={`text-sm font-medium text-gray-700 ${className}`}
      {...props}
    >
      {children}
    </label>
  );
};

const Input = ({ className, ...props }) => {
  return (
    <input
      className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${className}`}
      {...props}
    />
  );
};

const Textarea = ({ className, ...props }) => {
  return (
    <textarea
      className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${className}`}
      {...props}
    />
  );
};

const CardHeader = ({ className, children, ...props }) => {
  return (
    <div className={`p-6 border-b ${className}`} {...props}>
      {children}
    </div>
  );
};

const CardTitle = ({ className, children, ...props }) => {
  return (
    <h3 className={`text-lg font-medium text-gray-900 ${className}`} {...props}>
      {children}
    </h3>
  );
};

const Select = ({ children, value, onValueChange, ...props }) => {
  return (
    <div className="relative" {...props}>
      <select 
        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
      >
        {children}
      </select>
      <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );
};

const SelectTrigger = ({ children, className, ...props }) => {
  return (
    <div className={`relative ${className}`} {...props}>
      {children}
    </div>
  );
};

const SelectValue = ({ placeholder, className, ...props }) => {
  return (
    <span className={`block truncate ${className}`} {...props}>
      {placeholder}
    </span>
  );
};

const SelectContent = ({ children, className, ...props }) => {
  return (
    <div className={`mt-1 ${className}`} {...props}>
      {children}
    </div>
  );
};

const SelectItem = ({ children, value, className, ...props }) => {
  return (
    <option value={value} className={`py-1.5 px-2 ${className}`} {...props}>
      {children}
    </option>
  );
};

const Checkbox = ({ id, checked, onCheckedChange, className, ...props }) => {
  return (
    <input
      type="checkbox"
      id={id}
      checked={checked}
      onChange={(e) => onCheckedChange(e.target.checked)}
      className={`h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${className}`}
      {...props}
    />
  );
};

const RadioGroup = ({ value, onValueChange, children, className, ...props }) => {
  return (
    <div className={`${className}`} {...props}>
      {React.Children.map(children, child => {
        return React.cloneElement(child, {
          checked: child.props.value === value,
          onChange: () => onValueChange(child.props.value)
        });
      })}
    </div>
  );
};

const RadioGroupItem = ({ value, id, checked, onChange, className, ...props }) => {
  return (
    <input
      type="radio"
      id={id}
      value={value}
      checked={checked}
      onChange={onChange}
      className={`h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500 ${className}`}
      {...props}
    />
  );
};

const ScrollArea = ({ className, children, ...props }) => {
  return (
    <div className={`overflow-auto ${className}`} {...props}>
      {children}
    </div>
  );
};

// URL de Cloudflare para imágenes
const CLOUDFLARE_PROPERTIES_URL = 'https://pub-e51257e7234a40ea96269a0ab.r2.dev';

// Función de debounce para limitar la frecuencia de ejecución
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

const EditProyect = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { toast } = useToast();

  // Estados para manejo del proyecto
  const [proyecto, setProyecto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [images, setImages] = useState([]);
  const [proyectoStatus, setProyectoStatus] = useState('Activo');
  const [savingChanges, setSavingChanges] = useState(false);
  const [deletingProyecto, setDeletingProyecto] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  // Opciones para los selects
  const tiposProyecto = ['residencial', 'comercial', 'mixto'];
  const estadosProyecto = ['planos', 'lanzamiento', 'construccion', 'terminado'];
  const mesesEntrega = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
  const aniosEntrega = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear; i <= currentYear + 5; i++) {
    aniosEntrega.push(i.toString());
  }

  // Opciones de bancos
  const bancos = [
    'BCP', 'BBVA', 'Interbank', 'Scotiabank', 'Mi Banco', 
    'Banco Pichincha', 'Banco GNB', 'Banco Falabella', 'Otro'
  ];

  // Opciones de amenidades
  const amenidadesOptions = [
    'Piscina', 'Gimnasio', 'Área de BBQ', 'Sala de eventos', 'Parque infantil',
    'Zona de mascotas', 'Seguridad 24/7', 'Lobby', 'Coworking', 'Áreas verdes',
    'Estacionamiento', 'Ascensor', 'Terraza', 'Zona de lavandería', 'Otro'
  ];
  // Función para obtener la URL completa de la imagen
  const getFullImageUrl = useCallback((imageUrl) => {
    if (!imageUrl) return '/placeholder-property.jpg';
    if (imageUrl.startsWith('http')) return imageUrl;
    return `${CLOUDFLARE_PROPERTIES_URL}/${imageUrl}`;
  }, []);

  // Cargar datos del proyecto
  useEffect(() => {
    const fetchProyectoDetails = async () => {
      try {
        setLoading(true);
        const response = await fetch(`https://backend-production-1786.up.railway.app/api/proyectos/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('No se pudo cargar el proyecto');
        }

        const data = await response.json();

        // Verificar si el usuario actual es el propietario
        if (data.id_vendedor !== user.id) {
          toast({
            title: 'Acceso denegado',
            description: 'No tienes permiso para editar este proyecto',
            variant: 'destructive'
          });
          navigate('/mis_propiedades');
          return;
        }

        // Procesar los datos del proyecto
        const processedData = {
          ...data,
          // Convertir campos JSON a arrays si son strings
          bancos_financiamiento: typeof data.bancos_financiamiento === 'string' && data.bancos_financiamiento
            ? JSON.parse(data.bancos_financiamiento)
            : (data.bancos_financiamiento || []),
          amenidades: typeof data.amenidades === 'string' && data.amenidades
            ? JSON.parse(data.amenidades)
            : (data.amenidades || [])
        };

        setProyecto(processedData);
        
        // Procesar imágenes
        setImages(data.imagenes.map(img => ({
          url: getFullImageUrl(img),
          file: null,
          original: img
        })));
        
        setProyectoStatus(data.estado);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
        toast({
          title: 'Error',
          description: err.message,
          variant: 'destructive'
        });
        navigate('/mis_propiedades');
      }
    };

    if (user && token) {
      fetchProyectoDetails();
    }
  }, [id, user, token, navigate, toast, getFullImageUrl]);

  // Manejar cambios de imagen
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map(file => ({
      url: URL.createObjectURL(file),
      file,
      original: null
    }));

    // Limitar a 10 imágenes en total para proyectos
    const updatedImages = [...images, ...newImages].slice(0, 10);
    setImages(updatedImages);
  };

  const removeImage = (index) => {
    const updatedImages = [...images];
    updatedImages.splice(index, 1);
    setImages(updatedImages);
  };

  const moveImageUp = (index) => {
    if (index > 0) {
      const updatedImages = [...images];
      [updatedImages[index], updatedImages[index - 1]] = 
      [updatedImages[index - 1], updatedImages[index]];
      setImages(updatedImages);
    }
  };

  const moveImageDown = (index) => {
    if (index < images.length - 1) {
      const updatedImages = [...images];
      [updatedImages[index], updatedImages[index + 1]] = 
      [updatedImages[index + 1], updatedImages[index]];
      setImages(updatedImages);
    }
  };

  // Manejar cambios de estado del proyecto
  const handleProyectoStatusChange = (status) => {
    setProyectoStatus(status);
  };

  // Componente para manejar errores de carga de imágenes
  const PropertyImage = ({ url, alt, className }) => {
    const [error, setError] = useState(false);
  
    const handleError = () => {
      setError(true);
    };
  
    return (
      <img
        src={error ? '/placeholder-property.jpg' : url}
        alt={alt}
        className={className}
        onError={handleError}
      />
    );
  };

  // Función para manejar cambios en el formulario (con log espaciado)
  const logFieldChange = (name, value) => {
    console.log(`Campo actualizado: ${name} = ${value}`);
  };

  // Versión con debounce para evitar muchos logs
  const debouncedLogChange = useCallback(
    debounce(logFieldChange, 1000), // Espera 1 segundo antes de mostrar el log
    []
  );

  // Manejar cambios en los campos del formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProyecto(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Log con debounce para evitar sobrecargar la consola
    debouncedLogChange(name, value);
  };

  // Manejar cambios en selects
  const handleSelectChange = (name, value) => {
    setProyecto(prev => ({
      ...prev,
      [name]: value
    }));
    debouncedLogChange(name, value);
  };

  // Manejar cambios en los checkbox
  const handleCheckboxChange = (name, checked) => {
    setProyecto(prev => ({
      ...prev,
      [name]: checked
    }));
    debouncedLogChange(name, checked);
  };

  // Manejar cambios en los bancos de financiamiento
  const handleBancoChange = (banco, checked) => {
    if (checked) {
      setProyecto(prev => ({
        ...prev,
        bancos_financiamiento: [...(prev.bancos_financiamiento || []), banco]
      }));
    } else {
      setProyecto(prev => ({
        ...prev,
        bancos_financiamiento: (prev.bancos_financiamiento || []).filter(b => b !== banco)
      }));
    }
  };

  // Manejar cambios en las amenidades
  const handleAmenidadChange = (amenidad, checked) => {
    if (checked) {
      setProyecto(prev => ({
        ...prev,
        amenidades: [...(prev.amenidades || []), amenidad]
      }));
    } else {
      setProyecto(prev => ({
        ...prev,
        amenidades: (prev.amenidades || []).filter(a => a !== amenidad)
      }));
    }
  };

  // Manejar cambios en el mapa
  const handleMapChange = (location) => {
    setProyecto(prev => ({
      ...prev,
      latitude: location.lat,
      longitude: location.lng,
      mapa_direccion: location.address
    }));
  };

  // Guardar cambios en el proyecto
  const handleSaveChanges = async () => {
    try {
      setSavingChanges(true);

      // Preparar FormData para envío de imágenes
      const formData = new FormData();
      
      // Añadir datos básicos del proyecto
      if (proyecto) {
        // Campos de texto
        formData.append('id', proyecto.id);
        formData.append('nombre_proyecto', proyecto.nombre_proyecto || '');
        formData.append('descripcion_proyecto', proyecto.descripcion_proyecto || '');
        formData.append('tipo_proyecto', proyecto.tipo_proyecto || '');
        formData.append('estado_proyecto', proyecto.estado_proyecto || '');
        formData.append('ubicacion', proyecto.ubicacion || '');
        formData.append('mapa_direccion', proyecto.mapa_direccion || '');
        formData.append('mes_entrega', proyecto.mes_entrega || '');
        formData.append('anio_entrega', proyecto.anio_entrega || '');
        
        // Campos numéricos
        formData.append('total_unidades', proyecto.total_unidades || 0);
        
        // Unidades por tipo
        // 1 dormitorio
        formData.append('tiene_1dorm', proyecto.tiene_1dorm ? 1 : 0);
        if (proyecto.tiene_1dorm) {
          formData.append('unidades_1dorm', proyecto.unidades_1dorm || 0);
          formData.append('area_1dorm', proyecto.area_1dorm || 0);
          formData.append('precio_1dorm_soles', proyecto.precio_1dorm_soles || 0);
          formData.append('precio_1dorm_dolares', proyecto.precio_1dorm_dolares || 0);
        }
        
        // 2 dormitorios
        formData.append('tiene_2dorm', proyecto.tiene_2dorm ? 1 : 0);
        if (proyecto.tiene_2dorm) {
          formData.append('unidades_2dorm', proyecto.unidades_2dorm || 0);
          formData.append('area_2dorm', proyecto.area_2dorm || 0);
          formData.append('precio_2dorm_soles', proyecto.precio_2dorm_soles || 0);
          formData.append('precio_2dorm_dolares', proyecto.precio_2dorm_dolares || 0);
        }
        
        // 3 dormitorios
        formData.append('tiene_3dorm', proyecto.tiene_3dorm ? 1 : 0);
        if (proyecto.tiene_3dorm) {
          formData.append('unidades_3dorm', proyecto.unidades_3dorm || 0);
          formData.append('area_3dorm', proyecto.area_3dorm || 0);
          formData.append('precio_3dorm_soles', proyecto.precio_3dorm_soles || 0);
          formData.append('precio_3dorm_dolares', proyecto.precio_3dorm_dolares || 0);
        }
        
        // 4 dormitorios
        formData.append('tiene_4dorm', proyecto.tiene_4dorm ? 1 : 0);
        if (proyecto.tiene_4dorm) {
          formData.append('unidades_4dorm', proyecto.unidades_4dorm || 0);
          formData.append('area_4dorm', proyecto.area_4dorm || 0);
          formData.append('precio_4dorm_soles', proyecto.precio_4dorm_soles || 0);
          formData.append('precio_4dorm_dolares', proyecto.precio_4dorm_dolares || 0);
        }
        
        // Financiamiento
        formData.append('tiene_financiamiento', proyecto.tiene_financiamiento || 'No');
        formData.append('bancos_financiamiento', JSON.stringify(proyecto.bancos_financiamiento || []));
        formData.append('otro_banco', proyecto.otro_banco || '');
        formData.append('detalles_financiamiento', proyecto.detalles_financiamiento || '');
        
        // Amenidades
        formData.append('amenidades', JSON.stringify(proyecto.amenidades || []));
        formData.append('otras_caracteristicas', proyecto.otras_caracteristicas || '');
      }
      
      // Añadir estado actualizado
      formData.append('estado', proyectoStatus);

      // Añadir imágenes originales que se mantienen en el orden correcto
      const imagesToKeep = images
        .filter(img => img.original)
        .map(img => img.original);
      
      formData.append('imagenes_originales', JSON.stringify(imagesToKeep));
      
      // Añadir nuevas imágenes
      images
        .filter(img => img.file)
        .forEach((img) => {
          formData.append('nuevas_imagenes', img.file);
        });

      // Debug - Mostrar lo que se va a enviar
      console.log('Enviando datos de formulario:');
      for (let [key, value] of formData.entries()) {
        console.log(`${key}: ${value}`);
      }

      // Enviar datos al servidor
      const response = await fetch(`https://backend-production-1786.up.railway.app/api/proyectos/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al actualizar el proyecto');
      }

      // Mostrar mensaje de éxito
      toast({
        title: 'Proyecto actualizado',
        description: 'Los cambios se han guardado correctamente',
      });

      // Redirigir a la página de propiedades
      navigate('/mis_propiedades');
    } catch (error) {
      console.error('Error al guardar los cambios:', error);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setSavingChanges(false);
    }
  };
  // Eliminar proyecto
  const handleDeleteProyecto = async () => {
    try {
      setDeletingProyecto(true);
      
      const response = await fetch(`https://backend-production-1786.up.railway.app/api/proyectos/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al eliminar el proyecto');
      }

      // Mostrar mensaje de éxito
      toast({
        title: 'Proyecto eliminado',
        description: 'El proyecto ha sido eliminado correctamente'
      });

      // Redirigir a la página de propiedades
      navigate('/mis_propiedades');
    } catch (error) {
      console.error('Error al eliminar el proyecto:', error);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setDeletingProyecto(false);
      setShowDeleteConfirmation(false);
    }
  };

  // Renderizar contenido de la página de edición
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader className="animate-spin text-blue-500 w-12 h-12" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-red-50">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={() => navigate('/mis_propiedades')}
            className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600"
          >
            Volver a Mis Publicaciones
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-blue-100 pt-20 pb-10 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-6">
        {/* Cabecera del proyecto */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Building className="w-8 h-8 text-purple-600 mr-3" />
            <h1 className="text-2xl font-bold text-gray-800">
              Editar Proyecto Inmobiliario
            </h1>
          </div>
          <div className="text-sm text-gray-500">
            ID: {proyecto?.id}
          </div>
        </div>

        {/* Estado del Proyecto */}
        <div className="mb-6">
          <h2 className="text-xl font-bold mb-4">Estado del Proyecto</h2>
          <div className="flex space-x-4">
            <button
              onClick={() => handleProyectoStatusChange('Activo')}
              className={`px-4 py-2 rounded-lg transition-all ${
                proyectoStatus === 'Activo' 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-green-100'
              }`}
            >
              Activo
            </button>
            <button
              onClick={() => handleProyectoStatusChange('Inactivo')}
              className={`px-4 py-2 rounded-lg transition-all ${
                proyectoStatus === 'Inactivo' 
                  ? 'bg-red-500 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-red-100'
              }`}
            >
              Inactivo
            </button>
          </div>
        </div>

        {/* Pestañas de navegación */}
        <div className="mb-6 border-b">
          <div className="flex space-x-4">
            <button
              className={`px-4 py-2 font-medium border-b-2 ${
                activeTab === 'general' 
                  ? 'border-purple-500 text-purple-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('general')}
            >
              Información General
            </button>
            <button
              className={`px-4 py-2 font-medium border-b-2 ${
                activeTab === 'unidades' 
                  ? 'border-purple-500 text-purple-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('unidades')}
            >
              Unidades y Precios
            </button>
            <button
              className={`px-4 py-2 font-medium border-b-2 ${
                activeTab === 'financiamiento' 
                  ? 'border-purple-500 text-purple-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('financiamiento')}
            >
              Financiamiento
            </button>
            <button
              className={`px-4 py-2 font-medium border-b-2 ${
                activeTab === 'imagenes' 
                  ? 'border-purple-500 text-purple-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('imagenes')}
            >
              Imágenes
            </button>
          </div>
        </div>

        {/* Contenido según pestaña activa */}
        {activeTab === 'general' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Información General</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="nombre_proyecto">Nombre del Proyecto</Label>
                  <Input 
                    id="nombre_proyecto" 
                    name="nombre_proyecto" 
                    value={proyecto?.nombre_proyecto || ''} 
                    onChange={handleInputChange}
                    placeholder="Ej: Residencial Los Pinos"
                  />
                </div>

                <div>
                  <Label htmlFor="tipo_proyecto">Tipo de Proyecto</Label>
                  <Select 
                    value={proyecto?.tipo_proyecto} 
                    onValueChange={(value) => handleSelectChange('tipo_proyecto', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar tipo de proyecto" />
                    </SelectTrigger>
                    <SelectContent>
                      {tiposProyecto.map(tipo => (
                        <SelectItem key={tipo} value={tipo}>
                          {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="estado_proyecto">Estado del Proyecto</Label>
                  <Select 
                    value={proyecto?.estado_proyecto} 
                    onValueChange={(value) => handleSelectChange('estado_proyecto', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar estado del proyecto" />
                    </SelectTrigger>
                    <SelectContent>
                      {estadosProyecto.map(estado => (
                        <SelectItem key={estado} value={estado}>
                          {estado.charAt(0).toUpperCase() + estado.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="descripcion_proyecto">Descripción del Proyecto</Label>
                  <Textarea 
                    id="descripcion_proyecto" 
                    name="descripcion_proyecto" 
                    value={proyecto?.descripcion_proyecto || ''} 
                    onChange={handleInputChange}
                    placeholder="Describa las características principales del proyecto..."
                    rows={6}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="mes_entrega">Mes de Entrega</Label>
                    <Select 
                      value={proyecto?.mes_entrega} 
                      onValueChange={(value) => handleSelectChange('mes_entrega', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar mes" />
                      </SelectTrigger>
                      <SelectContent>
                        {mesesEntrega.map(mes => (
                          <SelectItem key={mes} value={mes}>{mes}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="anio_entrega">Año de Entrega</Label>
                    <Select 
                      value={proyecto?.anio_entrega} 
                      onValueChange={(value) => handleSelectChange('anio_entrega', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar año" />
                      </SelectTrigger>
                      <SelectContent>
                        {aniosEntrega.map(anio => (
                          <SelectItem key={anio} value={anio}>{anio}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="total_unidades">Total de Unidades</Label>
                  <Input 
                    id="total_unidades" 
                    name="total_unidades" 
                    type="number" 
                    value={proyecto?.total_unidades || ''} 
                    onChange={handleInputChange}
                    placeholder="Ej: 40"
                  />
                </div>

                <div>
                  <Label htmlFor="ubicacion">Ubicación</Label>
                  <Input 
                    id="ubicacion" 
                    name="ubicacion" 
                    value={proyecto?.ubicacion || ''} 
                    onChange={handleInputChange}
                    placeholder="Ej: San Isidro, Lima"
                  />
                </div>

                <div>
                  <Label>Ubicación en Mapa</Label>
                  <div className="h-64 mt-2 border rounded-md overflow-hidden">
                    <MapLocation 
                      initialLocation={{
                        lat: parseFloat(proyecto?.latitude) || -12.046374,
                        lng: parseFloat(proyecto?.longitude) || -77.042793,
                        address: proyecto?.mapa_direccion || ''
                      }}
                      onLocationChange={handleMapChange}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'unidades' && (
          <div className="space-y-6">
            {/* Unidades de 1 dormitorio */}
            <Card>
              <CardHeader>
                <CardTitle>Unidades de 1 Dormitorio</CardTitle>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="tiene_1dorm" 
                    checked={proyecto?.tiene_1dorm || false}
                    onCheckedChange={(checked) => handleCheckboxChange('tiene_1dorm', checked)}
                  />
                  <Label htmlFor="tiene_1dorm">Incluir este tipo</Label>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {proyecto?.tiene_1dorm && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="unidades_1dorm">Cantidad de Unidades</Label>
                      <Input 
                        id="unidades_1dorm" 
                        name="unidades_1dorm" 
                        type="number" 
                        value={proyecto?.unidades_1dorm || ''} 
                        onChange={handleInputChange}
                        placeholder="Ej: 10"
                      />
                    </div>
                    <div>
                      <Label htmlFor="area_1dorm">Área (m²)</Label>
                      <Input 
                        id="area_1dorm" 
                        name="area_1dorm" 
                        type="number" 
                        value={proyecto?.area_1dorm || ''} 
                        onChange={handleInputChange}
                        placeholder="Ej: 45"
                      />
                    </div>
                    <div>
                      <Label htmlFor="precio_1dorm_soles">Precio en Soles</Label>
                      <Input 
                        id="precio_1dorm_soles" 
                        name="precio_1dorm_soles" 
                        type="number" 
                        value={proyecto?.precio_1dorm_soles || ''} 
                        onChange={handleInputChange}
                        placeholder="Ej: 250000"
                      />
                    </div>
                    <div>
                      <Label htmlFor="precio_1dorm_dolares">Precio en Dólares</Label>
                      <Input 
                        id="precio_1dorm_dolares" 
                        name="precio_1dorm_dolares" 
                        type="number" 
                        value={proyecto?.precio_1dorm_dolares || ''} 
                        onChange={handleInputChange}
                        placeholder="Ej: 70000"
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Unidades de 2 dormitorios */}
            <Card>
              <CardHeader>
                <CardTitle>Unidades de 2 Dormitorios</CardTitle>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="tiene_2dorm" 
                    checked={proyecto?.tiene_2dorm || false}
                    onCheckedChange={(checked) => handleCheckboxChange('tiene_2dorm', checked)}
                  />
                  <Label htmlFor="tiene_2dorm">Incluir este tipo</Label>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {proyecto?.tiene_2dorm && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="unidades_2dorm">Cantidad de Unidades</Label>
                      <Input 
                        id="unidades_2dorm" 
                        name="unidades_2dorm" 
                        type="number" 
                        value={proyecto?.unidades_2dorm || ''} 
                        onChange={handleInputChange}
                        placeholder="Ej: 15"
                      />
                    </div>
                    <div>
                      <Label htmlFor="area_2dorm">Área (m²)</Label>
                      <Input 
                        id="area_2dorm" 
                        name="area_2dorm" 
                        type="number" 
                        value={proyecto?.area_2dorm || ''} 
                        onChange={handleInputChange}
                        placeholder="Ej: 65"
                      />
                    </div>
                    <div>
                      <Label htmlFor="precio_2dorm_soles">Precio en Soles</Label>
                      <Input 
                        id="precio_2dorm_soles" 
                        name="precio_2dorm_soles" 
                        type="number" 
                        value={proyecto?.precio_2dorm_soles || ''} 
                        onChange={handleInputChange}
                        placeholder="Ej: 320000"
                      />
                    </div>
                    <div>
                      <Label htmlFor="precio_2dorm_dolares">Precio en Dólares</Label>
                      <Input 
                        id="precio_2dorm_dolares" 
                        name="precio_2dorm_dolares" 
                        type="number" 
                        value={proyecto?.precio_2dorm_dolares || ''} 
                        onChange={handleInputChange}
                        placeholder="Ej: 90000"
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Unidades de 3 dormitorios */}
            <Card>
              <CardHeader>
                <CardTitle>Unidades de 3 Dormitorios</CardTitle>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="tiene_3dorm" 
                    checked={proyecto?.tiene_3dorm || false}
                    onCheckedChange={(checked) => handleCheckboxChange('tiene_3dorm', checked)}
                  />
                  <Label htmlFor="tiene_3dorm">Incluir este tipo</Label>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {proyecto?.tiene_3dorm && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="unidades_3dorm">Cantidad de Unidades</Label>
                      <Input 
                        id="unidades_3dorm" 
                        name="unidades_3dorm" 
                        type="number" 
                        value={proyecto?.unidades_3dorm || ''} 
                        onChange={handleInputChange}
                        placeholder="Ej: 12"
                      />
                    </div>
                    <div>
                      <Label htmlFor="area_3dorm">Área (m²)</Label>
                      <Input 
                        id="area_3dorm" 
                        name="area_3dorm" 
                        type="number" 
                        value={proyecto?.area_3dorm || ''} 
                        onChange={handleInputChange}
                        placeholder="Ej: 85"
                      />
                    </div>
                    <div>
                      <Label htmlFor="precio_3dorm_soles">Precio en Soles</Label>
                      <Input 
                        id="precio_3dorm_soles" 
                        name="precio_3dorm_soles" 
                        type="number" 
                        value={proyecto?.precio_3dorm_soles || ''} 
                        onChange={handleInputChange}
                        placeholder="Ej: 450000"
                      />
                    </div>
                    <div>
                      <Label htmlFor="precio_3dorm_dolares">Precio en Dólares</Label>
                      <Input 
                        id="precio_3dorm_dolares" 
                        name="precio_3dorm_dolares" 
                        type="number" 
                        value={proyecto?.precio_3dorm_dolares || ''} 
                        onChange={handleInputChange}
                        placeholder="Ej: 125000"
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Unidades de 4 dormitorios */}
            <Card>
              <CardHeader>
                <CardTitle>Unidades de 4 Dormitorios</CardTitle>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="tiene_4dorm" 
                    checked={proyecto?.tiene_4dorm || false}
                    onCheckedChange={(checked) => handleCheckboxChange('tiene_4dorm', checked)}
                  />
                  <Label htmlFor="tiene_4dorm">Incluir este tipo</Label>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {proyecto?.tiene_4dorm && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="unidades_4dorm">Cantidad de Unidades</Label>
                      <Input 
                        id="unidades_4dorm" 
                        name="unidades_4dorm" 
                        type="number" 
                        value={proyecto?.unidades_4dorm || ''} 
                        onChange={handleInputChange}
                        placeholder="Ej: 8"
                      />
                    </div>
                    <div>
                      <Label htmlFor="area_4dorm">Área (m²)</Label>
                      <Input 
                        id="area_4dorm" 
                        name="area_4dorm" 
                        type="number" 
                        value={proyecto?.area_4dorm || ''} 
                        onChange={handleInputChange}
                        placeholder="Ej: 120"
                      />
                    </div>
                    <div>
                      <Label htmlFor="precio_4dorm_soles">Precio en Soles</Label>
                      <Input 
                        id="precio_4dorm_soles" 
                        name="precio_4dorm_soles" 
                        type="number" 
                        value={proyecto?.precio_4dorm_soles || ''} 
                        onChange={handleInputChange}
                        placeholder="Ej: 580000"
                      />
                    </div>
                    <div>
                      <Label htmlFor="precio_4dorm_dolares">Precio en Dólares</Label>
                      <Input 
                        id="precio_4dorm_dolares" 
                        name="precio_4dorm_dolares" 
                        type="number" 
                        value={proyecto?.precio_4dorm_dolares || ''} 
                        onChange={handleInputChange}
                        placeholder="Ej: 160000"
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'financiamiento' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Financiamiento</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="tiene_financiamiento" className="mb-2 block">¿Tiene Financiamiento Bancario?</Label>
                  <RadioGroup 
                    id="tiene_financiamiento" 
                    value={proyecto?.tiene_financiamiento || 'No'} 
                    onValueChange={(value) => handleSelectChange('tiene_financiamiento', value)}
                    className="flex space-x-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Si" id="financiamiento-si" />
                      <Label htmlFor="financiamiento-si">Sí</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="No" id="financiamiento-no" />
                      <Label htmlFor="financiamiento-no">No</Label>
                    </div>
                  </RadioGroup>
                </div>

                {proyecto?.tiene_financiamiento === 'Si' && (
                  <>
                    <div>
                      <Label className="mb-2 block">Bancos que financian</Label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {bancos.map((banco) => (
                          <div key={banco} className="flex items-start space-x-2">
                            <Checkbox 
                              id={`banco-${banco}`} 
                              checked={(proyecto?.bancos_financiamiento || []).includes(banco)}
                              onCheckedChange={(checked) => handleBancoChange(banco, checked)}
                            />
                            <Label htmlFor={`banco-${banco}`} className="font-normal">
                              {banco}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {(proyecto?.bancos_financiamiento || []).includes('Otro') && (
                      <div>
                        <Label htmlFor="otro_banco">Especifique otro banco</Label>
                        <Input 
                          id="otro_banco" 
                          name="otro_banco" 
                          value={proyecto?.otro_banco || ''} 
                          onChange={handleInputChange}
                          placeholder="Nombre del banco"
                        />
                      </div>
                    )}

                    <div>
                      <Label htmlFor="detalles_financiamiento">Detalles del Financiamiento</Label>
                      <Textarea 
                        id="detalles_financiamiento" 
                        name="detalles_financiamiento" 
                        value={proyecto?.detalles_financiamiento || ''} 
                        onChange={handleInputChange}
                        placeholder="Describa detalles adicionales sobre el financiamiento..."
                        rows={4}
                      />
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Amenidades y Características</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="mb-2 block">Amenidades del Proyecto</Label>
                  <ScrollArea className="h-56 border rounded-md p-4">
                    <div className="grid grid-cols-2 gap-2">
                      {amenidadesOptions.map((amenidad) => (
                        <div key={amenidad} className="flex items-start space-x-2">
                          <Checkbox 
                            id={`amenidad-${amenidad}`} 
                            checked={(proyecto?.amenidades || []).includes(amenidad)}
                            onCheckedChange={(checked) => handleAmenidadChange(amenidad, checked)}
                          />
                          <Label htmlFor={`amenidad-${amenidad}`} className="font-normal">
                            {amenidad}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>

                <div>
                  <Label htmlFor="otras_caracteristicas">Otras Características</Label>
                  <Textarea 
                    id="otras_caracteristicas" 
                    name="otras_caracteristicas" 
                    value={proyecto?.otras_caracteristicas || ''} 
                    onChange={handleInputChange}
                    placeholder="Otras características o detalles del proyecto..."
                    rows={4}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'imagenes' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Imágenes del Proyecto</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {images.map((image, index) => (
                    <div 
                      key={index} 
                      className="relative group border rounded-lg overflow-hidden"
                    >
                      <PropertyImage 
                        url={image.url} 
                        alt={`Imagen ${index + 1}`} 
                        className="w-full h-40 object-cover"
                      />
                      <div className="absolute top-2 right-2 flex flex-col space-y-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button 
                          onClick={() => removeImage(index)}
                          className="bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        {index > 0 && (
                          <button 
                            onClick={() => moveImageUp(index)}
                            className="bg-blue-500 text-white p-1 rounded-full hover:bg-blue-600"
                          >
                            <ArrowUp className="w-4 h-4" />
                          </button>
                        )}
                        {index < images.length - 1 && (
                          <button 
                            onClick={() => moveImageDown(index)}
                            className="bg-blue-500 text-white p-1 rounded-full hover:bg-blue-600"
                          >
                            <ArrowDown className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {images.length < 10 && (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center h-40">
                      <input 
                        type="file" 
                        accept="image/*" 
                        multiple 
                        onChange={handleImageUpload}
                        className="hidden" 
                        id="upload-images"
                      />
                      <label 
                        htmlFor="upload-images"
                        className="cursor-pointer flex flex-col items-center text-gray-500 hover:text-blue-500"
                      >
                        <Camera className="w-12 h-12 mb-2" />
                        <span>Agregar Imagen</span>
                      </label>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Botones de Acción */}
        <div className="mt-8 flex justify-between">
          <button
            onClick={() => setShowDeleteConfirmation(true)}
            className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center gap-2"
            disabled={deletingProyecto}
          >
            {deletingProyecto ? (
              <Loader className="animate-spin w-4 h-4" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
            Eliminar Proyecto
          </button>
          
          <div className="flex space-x-4">
            <button
              onClick={() => navigate('/mis_propiedades')}
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cancelar
            </button>
            <button
              onClick={handleSaveChanges}
              className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2"
              disabled={savingChanges}
            >
              {savingChanges ? (
                <Loader className="animate-spin w-4 h-4" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Guardar Cambios
            </button>
          </div>
        </div>

        {/* Modal de Confirmación de Eliminación */}
        {showDeleteConfirmation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-8 max-w-md w-full">
              <h2 className="text-2xl font-bold mb-4 text-red-600">Confirmar Eliminación</h2>
              <p className="mb-6 text-gray-700">
                ¿Estás seguro de que deseas eliminar este proyecto? Esta acción no se puede deshacer.
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowDeleteConfirmation(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  disabled={deletingProyecto}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteProyecto}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center gap-2"
                  disabled={deletingProyecto}
                >
                  {deletingProyecto ? (
                    <Loader className="animate-spin w-4 h-4" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditProyect;